import { z } from 'zod';
import { IdSchema, InstantSchema } from '../common/index.js';

/** Confidentialité, consentements, sensibles, coulisses, RGPD (architecture §2.8). */

export const ConsentPurposeSchema = z
  .enum(['contact_cabinet', 'partage_numero_famille', 'partage_adresse_famille', 'canal_whatsapp', 'traitement_donnees'])
  .meta({ id: 'ConsentPurpose' });

export const ConsentSchema = z
  .object({
    id: IdSchema,
    personId: IdSchema,
    purpose: ConsentPurposeSchema,
    granted: z.boolean(),
    channel: z.enum(['whatsapp', 'email', 'portail', 'telephone', 'courrier']),
    capturedAt: InstantSchema,
  })
  .meta({ id: 'Consent', description: 'Consentement daté (ajout seul : l’état courant est le dernier).' });

export const ConsentRecordSchema = z
  .object({
    purpose: ConsentPurposeSchema,
    granted: z.boolean(),
    channel: z.enum(['whatsapp', 'email', 'portail', 'telephone', 'courrier']),
  })
  .meta({ id: 'ConsentRecord' });

/**
 * Destinataire proposable pour un partage hors de l'équipe (RFC-L55-001 point 1, ARB-VERROU-05) : un externe rattaché au dossier
 * (clerc ou notaire de son étude, prestataire de sa mission). Jamais envoyé d'office : l'équipe coche chaque nom.
 */
export const DestinatairePartageSchema = z
  .object({ userId: IdSchema, role: z.enum(['clerc', 'notaire', 'prestataire']), displayName: z.string().min(1) })
  .meta({ id: 'DestinatairePartage' });

export const SensitiveLabelSchema = z
  .object({
    id: IdSchema,
    personId: IdSchema,
    label: z.string().min(1),
    note: z.string().nullable(),
    shares: z.array(z.object({ recipientDisplayName: z.string(), grantedAt: InstantSchema, revokedAt: InstantSchema.nullable() })),
  })
  .meta({ id: 'SensitiveLabel', description: 'Étiquette sensible libre, visible de l’équipe MAAT seulement.' });

export const SensitiveShareSchema = z
  .object({
    recipients: z.array(z.object({ userId: IdSchema.nullable(), role: z.string().nullable(), displayName: z.string().min(1) })).min(1),
    acknowledgedRecipients: z.array(z.string().min(1)).min(1),
  })
  .refine((s) => s.recipients.every((r) => s.acknowledgedRecipients.includes(r.displayName)), {
    message: 'chaque destinataire doit avoir été affiché dans l’alerte',
    path: ['acknowledgedRecipients'],
  })
  .meta({ id: 'SensitiveShare', description: 'Partage hors équipe : la liste affichée dans l’alerte est enregistrée.' });

export const InternalNoteSchema = z
  .object({
    id: IdSchema,
    dossierId: IdSchema,
    personId: IdSchema.nullable(),
    body: z.string().min(1),
    isHypothesis: z.boolean(),
    authorDisplayName: z.string(),
    createdAt: InstantSchema,
  })
  .meta({ id: 'InternalNote', description: 'Coulisses : jamais exportées, jamais visibles des externes.' });

export const InternalNoteCreateSchema = z
  .object({ personId: IdSchema.nullable(), body: z.string().min(1), isHypothesis: z.boolean().default(false) })
  .meta({ id: 'InternalNoteCreate' });

export const PrivacyRequestCreateSchema = z
  .object({ kind: z.enum(['acces', 'rectification', 'suppression', 'opposition']), message: z.string().optional() })
  .meta({ id: 'PrivacyRequestCreate' });

export const PrivacyRequestSchema = z
  .object({
    id: IdSchema,
    kind: z.enum(['acces', 'rectification', 'suppression', 'opposition']),
    status: z.enum(['recue', 'en_cours', 'traitee']),
    receivedAt: InstantSchema,
  })
  .meta({ id: 'PrivacyRequest' });
